import { useEffect, useState } from 'react';
import {
    useNavigate,
    useLocation,
} from 'react-router-dom';
import { useLoadingScreen } from '../../../Store/LoadingManager';
import { useSnackbar } from "notistack";
import {
    Typography,
} from "@mui/material";

import { IconChevronLeft, IconDeviceFloppy } from '@tabler/icons-react';
import { DetailsContainer, PriceSettingsContainer } from '../../../Styles/Maintenance/priceSettingsStyles';

import { usePriceSettingsHook } from '../../../Hooks/Maintenance/PriceSettings/priceSettingsHooks';

import { PriceSetting, ModulePrice, UserTier, Storage, PriceSettingsInterface } from '../../../Models/Maintenance/PriceSettings/PriceSettings';
import { Authentication } from "../../../Models/Authentication/Authentication";

import RouteHelper from '../../../Utilities/Helpers/RouteHelper';
import AccessibilityHelper from '../../../Utilities/Helpers/AccessibilityHelper';
import GlobalFunctionHelper from '../../../Utilities/Helpers/GlobalFunctionHelper';
import LocalStorageHelper from '../../../Utilities/Helpers/LocalStorageHelper';

import { RouteEnum } from '../../../Utilities/Enums/RouteEnums';
import { CachingEnum } from '../../../Utilities/Enums/CachingEnum';
import { ButtonType } from '../../../Utilities/Enums/ButtonTypeEnum';
import { PriceSettingsField, AuthenticationField } from '../../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../../Utilities/Enums/AdminModulesEnum';
import { CurrencyEnum } from '../../../Utilities/Enums/CurrencyEnums';

import { ErrorMessage } from "../../../Constants/Messages";

import CustomDetailsCard from '../../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import CustomDeleteConfirmation from '../../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import CustomButton from '../../../CustomViews/Elements/CustomButton';
import CustomIconButton from '../../../CustomViews/Elements/CustomIconButton';
import FormTextField from '../../../CustomViews/Elements/FormTextField';
import CustomTabs from '../../../CustomViews/Elements/CustomTabs/CustomTabs';

import ModulePricesSettings from './ModulePricesSettings';
import UserTiersSettings from './UserTiersSettings';
import StoragesSettings from './StoragesSettings';

export default function PriceSettings() {
    let navigate = useNavigate();
    let location = useLocation();
    let { loading, setLoading } = useLoadingScreen();
    const { enqueueSnackbar } = useSnackbar();
    const _userData: Authentication = LocalStorageHelper.getUserData();
    // let requiredFields = RequiredParametersHelper.companyUser();

    //Use States
    let [formData, setformData] = useState<PriceSetting>(new PriceSetting());
    let [errorMessages, seterrorMessages] = useState({});
    let [openDelete, setopenDelete] = useState(false);
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);
    let [deleteType, setdeleteType] = useState<any>(null);
    let [moduleTempId, setmoduleTempId] = useState<number>(1);
    let [userTierTempId, setuserTierTempId] = useState<number>(1);
    let [storageTempId, setstorageTempId] = useState<number>(1);
    let [priceSettingsTab, setpriceSettingsTab] = useState<any>([]);
    let [selectedPriceSettingsTab, setselectedPriceSettingsTab] = useState<any>(_userData?.[AuthenticationField.CURRENCY_CODE] ?? CurrencyEnum.PHP);

    let [modulesCount, setmodulesCount] = useState<number>(1);
    let [userTierCount, setuserTierCount] = useState<number>(1);
    let [storageCount, setstorageCount] = useState<number>(1);
    
    const q_queryKey = [CachingEnum.PRICE_SETTINGS, {}];
    const { DropdownData, dropdownFetchStatus, getPriceSettings, createPriceSettings } = usePriceSettingsHook(q_queryKey, GlobalFunctionHelper.getCurrencyCode(), false);

    //Variables
    let priceSettingsProps: PriceSettingsInterface = {
        formData: formData,
        setformData: setformData,
        errorMessages: errorMessages,
        setopenDelete: setopenDelete,
        settoDeleteItem: settoDeleteItem,
        setdeleteType: setdeleteType,
        moduleTempId: moduleTempId,
        setmoduleTempId: setmoduleTempId,
        userTierTempId: userTierTempId,
        setuserTierTempId: setuserTierTempId,
        storageTempId: storageTempId,
        setstorageTempId: setstorageTempId,
        modulesCount: modulesCount,
        setmodulesCount: setmodulesCount,
        userTierCount: userTierCount,
        setuserTierCount: setuserTierCount,
        storageCount: storageCount,
        setstorageCount: setstorageCount,
        priceSettingsTab: priceSettingsTab,
        selectedPriceSettingsTab: selectedPriceSettingsTab,
    }

    let discountPercentageProps = {
        name: PriceSettingsField.ANNUAL_BILLING_DISCOUNT,
        title: 'Discount Percentage (%)',
        fieldValue: formData?.[PriceSettingsField.ANNUAL_BILLING_DISCOUNT] ?? "",
        type: 'number',
        disabled: !AccessibilityHelper.hasAccess(AdminModules.PRICE_SETTINGS_EDIT),
        // required: requiredFields?.find((field: any) => field.keyName == ModuleSettingField.NAME)?.keyName != null,
        errorMessages: errorMessages,
        handleOnChange: (selectedValue: any) => {
            setformData((prev: any) => ({ ...prev, [PriceSettingsField.ANNUAL_BILLING_DISCOUNT]: selectedValue ?? "" }));
        },
    };

    //Event Handlers
    const initializePriceSettings = async () => {
        let getCurrencyPriceSettings = await getPriceSettings({ currencyCode: selectedPriceSettingsTab });
        let _priceSetting: PriceSetting = getCurrencyPriceSettings?.data ?? new PriceSetting();

        let _modulesCount = _priceSetting?.[PriceSettingsField.MODULES]?.length ?? 0;
        let _userTierCount = _priceSetting?.[PriceSettingsField.USER_TIERS]?.length ?? 0;
        let _storageCount = _priceSetting?.[PriceSettingsField.STORAGES]?.length ?? 0;
        let _moduleTempId = moduleTempId;
        let _userTierTempId = userTierTempId;
        let _storageTempId = storageTempId;
        let _formData: PriceSetting = {
            ..._priceSetting,
            [PriceSettingsField.CURRENCY_CODE]: selectedPriceSettingsTab,
            [PriceSettingsField.MODULES]: _modulesCount > 0 ? _priceSetting?.[PriceSettingsField.MODULES]?.map((modItem: any, modIndex: number) => { 
                _moduleTempId = (modIndex == 0 ? moduleTempId : _moduleTempId + 1);
                return { ...modItem, tempId: _moduleTempId, [PriceSettingsField.MODULE_BASE_PRICE_CURRENCY_CODE]: selectedPriceSettingsTab }
            }) : [{...new ModulePrice(), [PriceSettingsField.MODULE_BASE_PRICE_CURRENCY_CODE]: selectedPriceSettingsTab }],
            [PriceSettingsField.USER_TIERS]: _userTierCount > 0 ? _priceSetting?.[PriceSettingsField.USER_TIERS]?.map((utItem: any, utIndex: number) => { 
                _userTierTempId = (utIndex == 0 ? userTierTempId : _userTierTempId + 1);
                return { ...utItem, tempId: _userTierTempId }
            }) : [new UserTier()],
            [PriceSettingsField.STORAGES]: _storageCount > 0 ? _priceSetting?.[PriceSettingsField.STORAGES]?.map((sItem: any, sIndex: number) => { 
                _storageTempId = (sIndex == 0 ? storageTempId : _storageTempId + 1);
                return { ...sItem, tempId: _storageTempId }
            }) : [new Storage()],
        }
        
        setformData(_formData);
        setmoduleTempId(_moduleTempId);
        setuserTierTempId(_userTierTempId);
        setstorageTempId(_storageTempId);
        setmodulesCount(_modulesCount > 0 ? _modulesCount : 1);
        setuserTierCount(_userTierCount > 0 ? _userTierCount : 1);
        setstorageCount(_storageCount > 0 ? _storageCount : 1);
    }

    const handleSubmit = async () => {
        let _modules: ModulePrice[] = [];
        let _userTiers: UserTier[] = [];
        let _storages: Storage[] = [];
        let hasError: boolean = false;
        let _errorMessages: any = errorMessages;

        if(modulesCount > 0) {
            formData?.[PriceSettingsField.MODULES]?.forEach((moduleItem: any) => {
                let doNotProceedModules = moduleItem?.[PriceSettingsField.MODULE_PUBLIC_ID] == null || moduleItem?.[PriceSettingsField.MODULE_PUBLIC_ID] == "";
                
                if(!doNotProceedModules) {
                    _modules.push(moduleItem);
                }
            });
        }

        if(userTierCount > 0) {
            formData?.[PriceSettingsField.USER_TIERS]?.forEach((userTierItem: any) => {
                let doNotProceedUserTier = !userTierItem?.[PriceSettingsField.MIN_USERS] || !userTierItem?.[PriceSettingsField.MAX_USERS];
                
                if(!doNotProceedUserTier) {
                    _userTiers.push(userTierItem);
                }
            });
        }

        if(storageCount > 0) {
            formData?.[PriceSettingsField.STORAGES]?.forEach((storageItem: any) => {
                let doNotProceedStorage = !storageItem?.[PriceSettingsField.STORAGE] || !storageItem?.[PriceSettingsField.STORAGE_PRICE];
                
                if(!doNotProceedStorage) {
                    _storages.push(storageItem);
                }
            });
        }

        if(_userTiers?.length > 0) {
            _userTiers?.forEach((item: UserTier, index: number) => {
                if((item?.[PriceSettingsField.MIN_USERS] ?? 0) > (item?.[PriceSettingsField.MAX_USERS] ?? 0)) {
                    _errorMessages = {
                        ...errorMessages,
                        [PriceSettingsField.MAX_USERS + item?.tempId]: ErrorMessage.MAX_USER_CANNOT_BE_LESS_THAN_MIN_USER,
                    };

                    hasError = true;
                } else {
                    delete _errorMessages?.[PriceSettingsField.MAX_USERS + item?.tempId];
                }
            });
        }

        seterrorMessages(_errorMessages);

        if(hasError) { enqueueSnackbar(ErrorMessage.FIELD_REQUIRED, { variant: 'error' }); return true;}

        let _formData = {
            ...formData,
            [PriceSettingsField.MODULES]: _modules,
            [PriceSettingsField.USER_TIERS]: _userTiers,
            [PriceSettingsField.STORAGES]: _storages
        };

        let createResponse = await createPriceSettings(JSON.stringify(_formData));
        // let _success: boolean = createResponse?.isSuccess ?? false;

        // if(_success) {
        // }
    }

    const handleDeleteItem = async () => {
        setLoading(true);

        if(deleteType == "modulePrice") {
            let _modules: ModulePrice[]  = formData?.[PriceSettingsField.MODULES] ?? [];

            _modules.splice(toDeleteItem?.index ?? 0, 1);

            setformData((prev: any) => ({ ...prev, [PriceSettingsField.MODULES]: [..._modules] }));
            setmodulesCount(_modules?.length ?? 0);
        }

        if(deleteType == "userTier") {
            let _userTiers: UserTier[]  = formData?.[PriceSettingsField.USER_TIERS] ?? [];
            let _newUserTiers: UserTier[] = [];

            _userTiers?.forEach((userTierItem: any, utIndex: any) => {
                let ut: UserTier = userTierItem;

                if(utIndex == toDeleteItem?.index + 1) {
                    let newMinUser = (_userTiers?.find((item: any, index: any) => index == toDeleteItem?.index - 1)?.[PriceSettingsField.MAX_USERS] ?? 0) + 1;

                    ut = {...ut, [PriceSettingsField.MIN_USERS]: newMinUser };
                }

                _newUserTiers.push(ut);
            });

            _newUserTiers.splice(toDeleteItem?.index ?? 0, 1);

            setformData((prev: any) => ({ ...prev, [PriceSettingsField.USER_TIERS]: [..._newUserTiers] }));
            setuserTierCount(_userTiers?.length ?? 0);
        }

        if(deleteType == "storage") {
            let _storages: Storage[]  = formData?.[PriceSettingsField.STORAGES] ?? [];

            _storages.splice(toDeleteItem?.index ?? 0, 1);

            setformData((prev: any) => ({ ...prev, [PriceSettingsField.STORAGES]: [..._storages] }));
            setstorageCount(_storages?.length ?? 0);
        }

        setopenDelete(false);
        settoDeleteItem(null);
        setdeleteType(null);
        setLoading(false);
    }

    useEffect(() => {
        if(!dropdownFetchStatus) {
            setpriceSettingsTab(DropdownData?.[CachingEnum.PRICE_SETTINGS_TAB_LIST] ?? []);
        }
    }, [dropdownFetchStatus, DropdownData]);

    useEffect(() => {
        initializePriceSettings();
    }, [selectedPriceSettingsTab]);
    
    //Rendering Functions
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.CANCEL}
                title='Back'
                startIcon={<IconChevronLeft />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    GlobalFunctionHelper.goBack(navigate, location, RouteEnum.PRICE_SETTING);
                }} />
            {AccessibilityHelper.hasAccess(AdminModules.PRICE_SETTINGS_EDIT) && <CustomButton
                btnType={ButtonType.SUCCESS}
                title='Save'
                startIcon={<IconDeviceFloppy />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    handleSubmit();
                }} />}
        </>
    }

    const renderAnnualBilling = () => {
        return <PriceSettingsContainer>
            <Typography className="settingItemHeader" color="primaryText">Annual Billing Discount</Typography>

            <FormTextField {...discountPercentageProps} />
        </PriceSettingsContainer>
    }

    return <DetailsContainer>
        <CustomDetailsCard
        title={RouteHelper.getRouteDescription(RouteEnum.PRICE_SETTING)}
        actionButtons={renderActionButtons()} >
            <CustomTabs
            selectedTab={selectedPriceSettingsTab}
            handleOnChange={(e: any) => {
                setselectedPriceSettingsTab(e);
                // setformData(priceSettingsTab?.find((item: any) => item.id == e)?.priceSettings ?? new PriceSetting());
            }}
            items={priceSettingsTab} />

            <ModulePricesSettings {...priceSettingsProps} />
            <UserTiersSettings {...priceSettingsProps} />
            <StoragesSettings {...priceSettingsProps} />
            {renderAnnualBilling()}
        </CustomDetailsCard>

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            ItemToDelete={toDeleteItem?.name ?? null} // Optional to display name of the item to be deleted
            handleClose={() => {
                setopenDelete(false);
                settoDeleteItem(null);
                setdeleteType(null);
            }}
            deleteItem={handleDeleteItem} />
        }
    </DetailsContainer>
}